// src/actions.ts
var ActionName = {
  createEquipment: "create-equipment",
  updateEquipment: "update-equipment",
  updateHomeOptions: "update-home-options",
  createCustomTask: "create-custom-task",
  createCustomEquipmentType: "create-custom-equipment-type",
  submitFeedback: "submit-feedback",
  pauseReminder: "pause-reminder",
  resumeReminder: "resume-reminder",
  adjustReminderFrequency: "adjust-reminder-frequency",
  skipNextReminder: "skip-next-reminder",
  rescheduleReminder: "reschedule-reminder",
  removeReminder: "remove-reminder",
  restoreReminder: "restore-reminder",
  surfaceReminderNow: "surface-reminder-now",
  fillWeekend: "fill-weekend",
  acceptConsiderations: "accept-considerations"
};
var ACTION_NAMES = Object.values(ActionName);

// src/ActionError.ts
var ActionError = class extends Error {
  userMessage;
  chatMessage;
  constructor(opts) {
    const { userMessage, chatMessage } = normalize(opts);
    super(userMessage || chatMessage);
    this.name = "ActionError";
    this.userMessage = userMessage;
    this.chatMessage = chatMessage;
  }
};
function normalize(opts) {
  if (typeof opts === "string") {
    return { userMessage: opts, chatMessage: opts };
  }
  const userMessage = opts.userMessage ?? opts.chatMessage ?? "";
  const chatMessage = opts.chatMessage ?? opts.userMessage ?? "";
  return { userMessage, chatMessage };
}

// src/chat-actions.ts
import { ChatMessageIdSchema } from "@rock/types";
import * as z11 from "zod";

// src/home/create-custom-task.types.ts
import {
  chatHintsRegistry,
  DashboardSpaceIdSchema,
  EquipmentIdSchema,
  LastPerformedEstimateSchema,
  LocalDateSchema,
  TaskSubscriptionSchema
} from "@rock/types";
import * as z from "zod";
var chat = chatHintsRegistry;
var CreateCustomTaskInputSchema = z.object({
  title: z.string().min(1).describe('Short imperative title for the task (e.g. "Replace HVAC filter").').register(chat, {
    commentary: 'If the user is vague ("defrost"), pick a clear title yourself ("Defrost ice maker") rather than asking. Required.'
  }),
  overview: z.string().nullish().describe("One- or two-sentence description of what the task entails and why.").register(chat, { commentary: "Optional \u2014 only fill when the user supplied detail worth preserving." }),
  subscription: TaskSubscriptionSchema.describe("How the task recurs (cadence + start). Required.").register(chat, {
    commentary: 'Clarify cadence with the user before drafting; do not silently default. Interval units are `days` | `weeks` | `months` | `years` \u2014 use the `weeks` unit for weekly cadences ("every two weeks" \u2192 `{ value: 2, unit: "weeks" }`); never convert weeks into days.'
  }),
  equipmentId: EquipmentIdSchema.nullish().register(chat, {
    commentary: "Set ONLY when the user mentions equipment AND there is exactly one match in the equipment list. If multiple match, ask in conversation. Leave null for home-wide tasks."
  }),
  spaceId: DashboardSpaceIdSchema.nullish().register(chat, {
    commentary: "When the user is talking about a specific space, copy the corresponding `spaceId` from a matched equipment entry in the home context. Leave null for whole-home tasks. Mutually informative with `equipmentId`."
  }),
  lastPerformedEstimate: LastPerformedEstimateSchema.nullish().describe(
    'When the task was last completed: a coarse bucket, or "specific-date" paired with `lastPerformedAt`. Drives the first scheduled occurrence. Omit to start the reminder now.'
  ).register(chat, {
    commentary: `Optional for recurring tasks. DEFAULT: if the user doesn't say when they last did it, OMIT this field \u2014 the reminder starts now (first occurrence lands this week). Do NOT ask when they last did it. Only set it when the user volunteers timing: a concrete or derivable date ("last Sunday", "this past weekend", "May 1") \u2192 set 'specific-date' AND \`lastPerformedAt\`; a vague recollection ("a few months ago") \u2192 a coarse bucket ('last-few-weeks', 'last-few-months', 'within-last-year', 'more-than-a-year') with \`lastPerformedAt\` left null. NEVER pair a bucket with a date. For one-time tasks leave null.`
  }),
  lastPerformedAt: LocalDateSchema.nullish().describe(
    'Calendar day the task was last performed (YYYY-MM-DD). Only valid with lastPerformedEstimate="specific-date".'
  ).register(chat, {
    commentary: `Resolve the user's phrasing ("last Sunday", "this past weekend", "3 weeks ago") to a calendar date (YYYY-MM-DD, no time) using today's date. Set ONLY together with lastPerformedEstimate="specific-date"; leave null for coarse buckets and for one-time tasks.`
  }),
  scheduledDate: LocalDateSchema.nullish().describe("Calendar day a one-time reminder should fire on (YYYY-MM-DD).").register(chat, {
    commentary: `User-provided calendar date for ONE-TIME tasks only (YYYY-MM-DD, no time). REQUIRED for one-time tasks: resolve relative phrasing ("in 3 months", "next spring") to a date using today's date, or ask "When do you want to be reminded?" before drafting if unstated. NEVER set for recurring tasks \u2014 recurring timing comes from lastPerformedEstimate.`
  })
});

// src/home/create-equipment.types.ts
import {
  BrandNameSchema,
  chatHintsRegistry as chatHintsRegistry2,
  DashboardSpaceIdSchema as DashboardSpaceIdSchema2,
  FolderIdSchema,
  EquipmentFieldsSchema,
  EquipmentIdSchema as EquipmentIdSchema2,
  EquipmentImageSchema,
  EquipmentTypeIdSchema,
  ImageSchema,
  ModelNumberSchema,
  RoomIdSchema
} from "@rock/types";
import * as z2 from "zod";
var chat2 = chatHintsRegistry2;
var CreateEquipmentInputSchema = z2.object({
  typeId: EquipmentTypeIdSchema.register(chat2, {
    commentary: "Required. Resolve before drafting \u2014 never call this action without a real catalog id."
  }),
  // Form-only: the chat agent never sets the space. Chat omits it and the action falls back to
  // the type's default/hint space. See the action.
  spaceId: DashboardSpaceIdSchema2.nullish().describe("Target space. Omit for chat \u2014 derived server-side from placement or the type's default space.").register(chat2, { formOnly: true }),
  nickname: z2.string().nullish().describe('Optional user-friendly nickname (e.g. "Living-room TV", "Garage fridge").').register(chat2, {
    commentary: "Set ONLY when the user has clearly given a nickname; do not invent. Leave null otherwise."
  }),
  roomId: RoomIdSchema.nullish().register(chat2, {
    commentary: "Set only when the user named one or context makes it unambiguous. If no match in the rooms list, leave null. If multiple rooms match (e.g. two bedrooms), leave null \u2014 the tool will ask for disambiguation via chips."
  }),
  modelNumber: ModelNumberSchema.nullish().describe("Manufacturer model number for this piece of equipment.").register(chat2, {
    commentary: "Set only if the user has stated it \u2014 triggers an automatic background lookup of model details. Leave null otherwise."
  }),
  brand: BrandNameSchema.nullish().describe("Brand name as observed (free text, not a catalog id).").register(chat2, {
    commentary: 'The brand name exactly as the user stated it (e.g. "GE"). No catalog resolution needed.'
  }),
  productName: z2.string().nullish().describe("The name the product was sold under, for equipment that has no model number.").register(chat2, {
    commentary: 'Set only when the user names the product rather than a model number (e.g. "Polar Monkeys Star Treatment 2.0" \u2014 put the brand in `brand` and the rest here). Triggers the same background lookup a model number does. Leave null otherwise.'
  }),
  // --- Form-only fields ---
  // The chat agent never sees these (stripped from the OpenAI tool spec
  // by `chatToolFromAction`). Form-path callers pass them in directly.
  equipmentId: EquipmentIdSchema2.nullish().describe(
    "Caller-pre-allocated equipment id. Form-only: when the caller commits an image draft before invoking the action (the upload queue needs the final firestoreDocPath, which encodes the equipmentId), the same id is supplied here so the action writes the doc at the path the upload is targeting. Omitted \u2192 the action mints one."
  ).register(chat2, { formOnly: true }),
  folderId: FolderIdSchema.nullish().describe(
    "Caller-resolved folder id. Form-only: the system / folder flows pre-create the folder and pass it in here; omitted \u2192 the equipment lands at root (grouped at render time)."
  ).register(chat2, { formOnly: true }),
  fields: EquipmentFieldsSchema.nullish().describe(
    "Arbitrary `EquipmentFields` (e.g. guide-derived `quantity`, the `Record<DataKey, GuideValue>` half of EquipmentFields). Form-only: merged over the curated subset (`roomId`, `modelNumber`, `brand`) inside the action body."
  ).register(chat2, { formOnly: true }),
  image: ImageSchema.nullish().describe(
    "Optional caller-supplied image. Form-only: the action stamps it with a fresh id so duplicate URLs across multiple equipment instances stay distinct."
  ).register(chat2, { formOnly: true }),
  modelNumberImage: EquipmentImageSchema.nullish().describe(
    'Optional photo of the model-number plate, saved as a secondary equipment image. PRECONDITION: caller MUST provide a globally-unique `id` (the client-side upload queue matches by it). The scan flow generates one via `processCapturedPhoto`; any future caller \u2014 including non-form ones \u2014 must do the same. The action stamps `category="model-number"` but does NOT re-stamp the id.'
  ).register(chat2, { formOnly: true }),
  serialNumberImage: EquipmentImageSchema.nullish().describe(
    'Optional photo of the serial-number plate, saved as a secondary equipment image. PRECONDITION: caller MUST provide a globally-unique `id` (the client-side upload queue matches by it). The scan flow generates one via `processCapturedPhoto`; any future caller \u2014 including non-form ones \u2014 must do the same. The action stamps `category="serial-number"` but does NOT re-stamp the id.'
  ).register(chat2, { formOnly: true })
});

// src/home/update-equipment.types.ts
import {
  BrandNameSchema as BrandNameSchema2,
  chatHintsRegistry as chatHintsRegistry3,
  DashboardSpaceIdSchema as DashboardSpaceIdSchema3,
  DataKeySchema,
  FolderIdSchema as FolderIdSchema2,
  EquipmentFieldsSchema as EquipmentFieldsSchema2,
  EquipmentIdSchema as EquipmentIdSchema3,
  EquipmentImageSchema as EquipmentImageSchema2,
  ModelNumberSchema as ModelNumberSchema2,
  RoomIdSchema as RoomIdSchema2,
  SerialConfirmationSchema
} from "@rock/types";
import * as z3 from "zod";
var chat3 = chatHintsRegistry3;
var UpdateEquipmentInputSchema = z3.object({
  equipmentId: EquipmentIdSchema3.register(chat3, {
    commentary: "Required. If no equipment matches the user's reference (or multiple do), ask which one before drafting."
  }),
  // Accepted-but-ignored for addressing (equipment is addressed by id alone; the doc's own
  // `spaceId` field is the space truth). Kept so in-flight chat drafts and older clients that
  // still send it keep validating.
  spaceId: DashboardSpaceIdSchema3.nullish().register(chat3, {
    commentary: "Optional and ignored \u2014 the equipment id alone identifies the target."
  }),
  nickname: z3.string().nullish().describe("User-friendly nickname for this equipment instance.").register(chat3, {
    commentary: "Set when the user has explicitly named it. Pass an empty string to clear the existing nickname; null leaves it unchanged."
  }),
  modelNumber: ModelNumberSchema2.nullish().describe("Manufacturer model number for this piece of equipment.").register(chat3, {
    commentary: "Set only if the user stated one \u2014 triggers a background lookup. Leave null to leave the existing value alone."
  }),
  brand: BrandNameSchema2.nullish().describe("Brand name as observed (free text, not a catalog id).").register(chat3, {
    commentary: 'The brand name exactly as the user stated it (e.g. "GE"). Leave null to leave the existing value alone.'
  }),
  productName: z3.string().nullish().describe("The name the product was sold under, for equipment that has no model number.").register(chat3, {
    commentary: "Set only when the user names the product rather than a model number \u2014 triggers a background lookup. Leave null to leave the existing value alone."
  }),
  roomId: RoomIdSchema2.nullish().register(chat3, {
    commentary: "Leave null to leave the existing room alone; only set when the user explicitly asked to move the equipment to a different room."
  }),
  active: z3.boolean().nullish().describe("Whether the equipment is active in the home.").register(chat3, {
    commentary: "Set to `false` to soft-delete (user replaced or removed it), `true` to re-activate. Leave null to leave it alone."
  }),
  // --- Form-only fields ---
  // The chat agent never sees these (stripped from the OpenAI tool spec
  // by `chatToolFromAction`). Form-path callers pass them in directly.
  folderId: FolderIdSchema2.nullable().optional().describe(
    "Folder membership. Form-only: the move flow resolves-or-creates the destination folder client-side and passes it here; the action verifies it exists before writing. Explicit `null` moves the equipment OUT of its folder (back to root); omitted leaves membership alone."
  ).register(chat3, { formOnly: true }),
  fields: EquipmentFieldsSchema2.nullish().describe(
    "Arbitrary `EquipmentFields` (the `Record<DataKey, GuideValue>` half of EquipmentFields plus guide-derived keys like `quantity`). Form-only: merged over the existing `equipment.fields` and over the curated subset (`roomId`, `modelNumber`, `brand`) inside the action body."
  ).register(chat3, { formOnly: true }),
  modelNumberImage: EquipmentImageSchema2.nullish().describe(
    'Optional photo of the model-number plate. Form-only: replaces any existing image with category="model-number" on the equipment; other images are untouched. Pass null to clear the existing model-number image. PRECONDITION: caller MUST provide a globally-unique `id` (the client-side upload queue matches by it); the action does NOT re-stamp.'
  ).register(chat3, { formOnly: true }),
  serialNumberImage: EquipmentImageSchema2.nullish().describe(
    'Optional photo of the serial-number plate. Form-only: replaces any existing image with category="serial-number" on the equipment; other images are untouched. Pass null to clear the existing serial-number image. PRECONDITION: caller MUST provide a globally-unique `id` (the client-side upload queue matches by it); the action does NOT re-stamp.'
  ).register(chat3, { formOnly: true }),
  hiddenCaptureFields: z3.array(DataKeySchema).nullish().describe(
    "Full replacement list of capture-CTA dataKeys the user hid on the equipment dashboard. Form-only: pass the complete desired list (not a delta); null leaves the existing list alone."
  ).register(chat3, { formOnly: true }),
  serialConfirmation: SerialConfirmationSchema.nullish().describe(
    "User-picked manufacture year resolving an ambiguous serial-number decode (see @rock/serials \u2014 the decode ignores it once stale, so no cleanup on serial edits). Form-only: set from the decode dialog, where the serial's candidate years are known. Pass null to clear."
  ).register(chat3, { formOnly: true })
});
var UpdateEquipmentBeforeSchema = z3.object({
  nickname: z3.string().nullish(),
  modelNumber: z3.string().nullish(),
  brand: z3.string().nullish(),
  productName: z3.string().nullish()
});
function snapshotEquipmentBefore(equipment) {
  return {
    nickname: equipment.nickname ?? null,
    modelNumber: equipment.fields?.modelNumber ?? null,
    brand: equipment.fields?.brand ?? null,
    productName: equipment.fields?.productName ?? null
  };
}

// src/reminders/adjust-reminder-frequency.types.ts
import { chatHintsRegistry as chatHintsRegistry4, HomeTaskSubscriptionIdSchema, TaskSubscriptionIntervalSchema } from "@rock/types";
import * as z4 from "zod";
var chat4 = chatHintsRegistry4;
var AdjustReminderFrequencyInputSchema = z4.object({
  subscriptionId: HomeTaskSubscriptionIdSchema.describe(
    "The id of the reminder (`HomeTaskSubscription`) whose cadence should change."
  ).register(chat4, {
    commentary: "Copy from the home context's reminders entry the user is referring to. The reminder must be recurring; one-time reminders have no cadence to adjust."
  }),
  interval: TaskSubscriptionIntervalSchema.describe(
    'New cadence: `{ value: number, unit: "days" | "months" | "years" }`. Use days for weekly cadence (multiply weeks \xD7 7).'
  ).register(chat4, {
    commentary: 'Confirm the new cadence with the user before drafting if at all ambiguous. Map "every couple months" \u2192 `{ value: 2, unit: "months" }`; "weekly" \u2192 `{ value: 7, unit: "days" }`.'
  })
});

// src/reminders/pause-reminder.types.ts
import { chatHintsRegistry as chatHintsRegistry5, HomeTaskSubscriptionIdSchema as HomeTaskSubscriptionIdSchema2 } from "@rock/types";
import * as z5 from "zod";
var chat5 = chatHintsRegistry5;
var PauseReminderInputSchema = z5.object({
  subscriptionId: HomeTaskSubscriptionIdSchema2.describe(
    "The id of the reminder (`HomeTaskSubscription`) to pause."
  ).register(chat5, {
    commentary: "Copy from the home context's reminders entry the user is referring to. Never invent or guess; if there's no clear match, ask the user which reminder."
  })
});

// src/reminders/remove-reminder.types.ts
import { chatHintsRegistry as chatHintsRegistry6, HomeTaskSubscriptionIdSchema as HomeTaskSubscriptionIdSchema3 } from "@rock/types";
import * as z6 from "zod";
var chat6 = chatHintsRegistry6;
var RemoveReminderInputSchema = z6.object({
  subscriptionId: HomeTaskSubscriptionIdSchema3.describe(
    "The id of the reminder (`HomeTaskSubscription`) to remove. Past completions remain in history; future occurrences are cancelled."
  ).register(chat6, {
    commentary: "Copy from the home context's reminders entry the user is referring to. Removal is a soft delete (status: 'deleted'); the reminder disappears from the user's lists but its history is preserved. Confirm intent before drafting."
  })
});

// src/reminders/reschedule-reminder.types.ts
import { chatHintsRegistry as chatHintsRegistry7, HomeTaskSubscriptionIdSchema as HomeTaskSubscriptionIdSchema4, LocalDateSchema as LocalDateSchema2 } from "@rock/types";
import * as z7 from "zod";
var chat7 = chatHintsRegistry7;
var RescheduleReminderInputSchema = z7.object({
  subscriptionId: HomeTaskSubscriptionIdSchema4.describe(
    "The id of the reminder (`HomeTaskSubscription`) whose next occurrence should be moved."
  ).register(chat7, {
    commentary: "Copy from the home context's reminders entry the user is referring to. If the user wants to skip outright (not move), use `action-skip-next-reminder` instead."
  }),
  newTaskDate: LocalDateSchema2.describe(
    "Calendar date (`YYYY-MM-DD`) the next occurrence should move to. If a task has already been generated, future occurrences cascade by the same number of days; if the occurrence is still scheduled but not yet generated, its scheduled date moves instead."
  ).register(chat7, {
    commentary: "Resolve the user's relative phrasing to an absolute calendar date (`YYYY-MM-DD`) before drafting. \"Next Friday\" \u2192 that Friday's date. Confirm with the user if ambiguous."
  })
});

// src/reminders/resume-reminder.types.ts
import { chatHintsRegistry as chatHintsRegistry8, HomeTaskSubscriptionIdSchema as HomeTaskSubscriptionIdSchema5 } from "@rock/types";
import * as z8 from "zod";
var chat8 = chatHintsRegistry8;
var ResumeReminderInputSchema = z8.object({
  subscriptionId: HomeTaskSubscriptionIdSchema5.describe(
    "The id of the reminder (`HomeTaskSubscription`) to resume (move back to active)."
  ).register(chat8, {
    commentary: "Copy from the home context's reminders entry the user is referring to. Use this only for a previously paused (or otherwise non-active) reminder."
  })
});

// src/reminders/skip-next-reminder.types.ts
import { chatHintsRegistry as chatHintsRegistry9, HomeTaskSubscriptionIdSchema as HomeTaskSubscriptionIdSchema6 } from "@rock/types";
import * as z9 from "zod";
var chat9 = chatHintsRegistry9;
var SkipNextReminderInputSchema = z9.object({
  subscriptionId: HomeTaskSubscriptionIdSchema6.describe(
    "The id of the reminder (`HomeTaskSubscription`) whose next scheduled occurrence should be skipped."
  ).register(chat9, {
    commentary: "Copy from the home context's reminders entry the user is referring to. Skip only the next occurrence; the cadence continues afterward. If the user wants to shift the date instead, use `action-reschedule-reminder`."
  })
});

// src/support/submit-feedback.types.ts
import {
  chatHintsRegistry as chatHintsRegistry10,
  FeedbackBodyFeedbackSchema,
  FeedbackBodyIdeaSchema,
  FeedbackBodyIssueSchema,
  FeedbackBodyOtherSchema,
  FeedbackBodyQuestionSchema
} from "@rock/types";
import * as z10 from "zod";
var chat10 = chatHintsRegistry10;
var FeedbackBodyChatSchema = z10.discriminatedUnion("type", [
  FeedbackBodyFeedbackSchema,
  FeedbackBodyIdeaSchema,
  FeedbackBodyIssueSchema,
  FeedbackBodyQuestionSchema,
  FeedbackBodyOtherSchema
]).describe(
  "REQUIRED nested object \u2014 exactly one variant chosen by `type`. Do NOT spread these fields at the top level of the tool call; they must be wrapped under `body`."
).register(chat10, {
  commentary: "Pick `type` from the user's intent \u2014 don't ask them to choose. Auto-fill the body fields from what they've already said in this chat; never make them retype anything. For `idea`/`issue`, write the `summary` yourself (\u22648 words) and put the user's longer wording in `details`. For `feedback`/`question`/`other`, drop the user's words straight into `message`/`question`. Variants: feedback (appreciation / thoughts), idea (feature request), issue (bug / unexpected behavior), question (wants a human follow-up), other (none of the above \u2014 rare)."
});
var SubmitFeedbackInputSchema = z10.object({
  body: FeedbackBodyChatSchema,
  equipment: z10.string().nullish().describe("Optional `spaceId/equipmentId` reference if the message is about a specific piece of equipment.").register(chat10, {
    commentary: "OPTIONAL. Set only if the user is clearly referring to a specific piece of equipment from their home \u2014 match `spaceId/equipmentId` from home context. Otherwise leave null."
  })
});

// src/chat-actions.ts
var CHAT_ACTION_INPUT_SCHEMAS = {
  [ActionName.createEquipment]: CreateEquipmentInputSchema,
  [ActionName.updateEquipment]: UpdateEquipmentInputSchema,
  [ActionName.createCustomTask]: CreateCustomTaskInputSchema,
  [ActionName.submitFeedback]: SubmitFeedbackInputSchema,
  [ActionName.pauseReminder]: PauseReminderInputSchema,
  [ActionName.resumeReminder]: ResumeReminderInputSchema,
  [ActionName.adjustReminderFrequency]: AdjustReminderFrequencyInputSchema,
  [ActionName.skipNextReminder]: SkipNextReminderInputSchema,
  [ActionName.rescheduleReminder]: RescheduleReminderInputSchema,
  [ActionName.removeReminder]: RemoveReminderInputSchema
};
var CHAT_ACTION_NAMES = Object.keys(CHAT_ACTION_INPUT_SCHEMAS);
var ChatActionNameSchema = z11.enum(CHAT_ACTION_NAMES);
function actionToolName(action) {
  return `action-${action}`;
}
var ACTION_TOOL_NAMES = CHAT_ACTION_NAMES.map(actionToolName);
function isActionToolName(name) {
  return ACTION_TOOL_NAMES.includes(name);
}
function draftVariant(name) {
  return z11.object({
    status: z11.literal("draft"),
    action: z11.literal(name),
    input: CHAT_ACTION_INPUT_SCHEMAS[name],
    // For the agent, as on the failure envelopes: how to reply about a draft. Absent on older drafts.
    chatMessage: z11.string().optional()
  });
}
var ChatActionDraftSchema = z11.discriminatedUnion("action", [
  draftVariant(ActionName.createEquipment),
  // Absent on older drafts.
  draftVariant(ActionName.updateEquipment).extend({ before: UpdateEquipmentBeforeSchema.optional() }),
  draftVariant(ActionName.createCustomTask),
  draftVariant(ActionName.submitFeedback),
  draftVariant(ActionName.pauseReminder),
  draftVariant(ActionName.resumeReminder),
  draftVariant(ActionName.adjustReminderFrequency),
  draftVariant(ActionName.skipNextReminder),
  draftVariant(ActionName.rescheduleReminder),
  draftVariant(ActionName.removeReminder)
]);
var ChatActionDraftFailureSchema = z11.object({
  status: z11.literal("draft-failed"),
  action: ChatActionNameSchema,
  chatMessage: z11.string()
});
var ChatActionCommitSuccessSchema = z11.object({
  status: z11.literal("committed"),
  action: ChatActionNameSchema,
  // Optional key, not just undefined value: zod 4.4+ rejects a missing key for a non-optional z.unknown().
  result: z11.unknown().optional(),
  // The draft message whose card the user confirmed. Absent when the surface tracks no drafts (CLI, tests).
  draftMessageId: ChatMessageIdSchema.optional()
});
var ChatActionCommitFailureSchema = z11.object({
  status: z11.literal("commit-failed"),
  action: ChatActionNameSchema,
  chatMessage: z11.string()
});
var ChatToolEnvelopeSchema = z11.union([
  ChatActionDraftSchema,
  ChatActionDraftFailureSchema,
  ChatActionCommitSuccessSchema,
  ChatActionCommitFailureSchema
]);

// src/chat-live-draft.ts
function findLiveDraft(conversation) {
  const index = conversation.messages.findLastIndex((m) => isEnvelope(m, "draft"));
  const message = conversation.messages[index];
  if (!message || conversation.committedDraftIds[message.id] || conversation.cancelledDraftIds[message.id]) {
    return void 0;
  }
  if (conversation.messages.slice(index + 1).some((m) => isEnvelope(m, "committed"))) return void 0;
  const draft2 = ChatActionDraftSchema.safeParse(message.toolResult?.result);
  return draft2.success ? { messageId: message.id, draft: draft2.data } : void 0;
}
function isEnvelope(message, status) {
  if (message.role !== "tool-result") return false;
  const result = message.toolResult?.result;
  return typeof result === "object" && result !== null && "status" in result && result.status === status;
}

// src/defineAction.ts
function defineAction(action) {
  if (!action.validate) return action;
  const userValidate = action.validate;
  const userExecute = action.execute;
  const wrappedExecute = async (input, ctx) => {
    await userValidate(input, ctx);
    return userExecute(input, ctx);
  };
  return {
    name: action.name,
    input: action.input,
    validate: action.validate,
    execute: wrappedExecute
  };
}

// src/dispatchCommit.ts
import { assertUnreachable } from "@rock/types";

// src/home/create-custom-task.action.ts
import { equipmentIdQuery } from "@rock/hql";
import { windowFitsCadence } from "@rock/tasks";
import {
  CUSTOM_ID_PREFIX,
  SpaceIdSchema
} from "@rock/types";

// src/validators/validateEquipmentId.ts
async function validateEquipmentId(equipmentId, ctx) {
  const matched = await ctx.daos.equipment.getEquipment(ctx.homeId, equipmentId);
  if (!matched) {
    throw new ActionError({
      userMessage: `Hmm, we couldn't find that equipment.`,
      chatMessage: `equipmentId "${equipmentId}" was not found in this home. Copy the equipmentId from the home context's equipment list.`
    });
  }
}

// src/home/create-custom-task.action.ts
var createCustomTaskAction = defineAction({
  name: "create-custom-task",
  input: CreateCustomTaskInputSchema,
  // Business rules. Runs before `execute` (via the defineAction wrapper) and
  // directly from chat-draft mode.
  validate: async (input, ctx) => {
    if (input.lastPerformedEstimate === "specific-date" && !input.lastPerformedAt) {
      throw new ActionError({
        userMessage: `Pick the date you last did it.`,
        chatMessage: `lastPerformedEstimate is "specific-date" but lastPerformedAt is missing. Resolve the user's phrasing to a calendar date (YYYY-MM-DD), set lastPerformedAt, then redraft.`
      });
    }
    if (input.lastPerformedAt && input.lastPerformedEstimate !== "specific-date") {
      throw new ActionError({
        userMessage: `Choose either an estimate or a specific date, not both.`,
        chatMessage: `lastPerformedAt was set but lastPerformedEstimate is "${input.lastPerformedEstimate ?? "null"}". A date pairs only with lastPerformedEstimate="specific-date" \u2014 either drop lastPerformedAt and keep the bucket, or switch the estimate to "specific-date".`
      });
    }
    if (input.subscription.type === "one-time" && !input.scheduledDate) {
      throw new ActionError({
        userMessage: `One-time reminders need a date`,
        chatMessage: `One-time tasks need a scheduledDate (the calendar date (YYYY-MM-DD) the reminder should fire). Ask the user "when do you want to be reminded?" and redraft with that date.`
      });
    }
    if (input.subscription.type === "recurring" && input.scheduledDate) {
      throw new ActionError({
        userMessage: `Recurring reminders can't be pinned to a single date`,
        chatMessage: `Recurring tasks must not set scheduledDate \u2014 their timing comes from lastPerformedEstimate plus the interval. Drop scheduledDate and redraft.`
      });
    }
    if (!windowFitsCadence(input.subscription)) {
      throw new ActionError({
        userMessage: `That season doesn't fit how often the reminder repeats`,
        chatMessage: `A subscription window must fit its interval: any window on a yearly cadence, one separate season per occurrence on a sub-year one (every 6 months \u2192 two seasons, e.g. ["spring", "fall"]), none on days or weeks. Fix the window or drop it, and redraft.`
      });
    }
    if (input.equipmentId) {
      await validateEquipmentId(input.equipmentId, ctx);
    }
  },
  execute: async (input, ctx) => {
    const { customTaskType, homeTaskSubscription } = ctx.daos;
    const spaceId = input.spaceId ? SpaceIdSchema.parse(input.spaceId) : void 0;
    const nowIso = (/* @__PURE__ */ new Date()).toISOString();
    const taskTypeId = `${CUSTOM_ID_PREFIX}${ctx.idGen()}`;
    const customTaskTypeDoc = {
      object: "custom-task-type",
      schema: 1,
      id: taskTypeId,
      homeId: ctx.homeId,
      createdBy: ctx.userId,
      title: input.title.trim(),
      overview: input.overview?.trim() || void 0,
      subscription: input.subscription,
      appliesTo: input.equipmentId ? equipmentIdQuery(input.equipmentId) : "home",
      createdAt: nowIso,
      updatedAt: nowIso
    };
    await customTaskType.setCustomTaskType(ctx.homeId, taskTypeId, customTaskTypeDoc);
    const subscriptionId = ctx.idGen();
    const homeSubscription = {
      object: "home-task-subscription",
      schema: 1,
      id: subscriptionId,
      homeId: ctx.homeId,
      taskTypeId,
      subscription: input.subscription,
      spaceId,
      status: "active",
      // lastPerformedEstimate (and its paired calendar day) only apply to recurring
      // schedules — drop them on one-time tasks so they don't silently persist.
      lastPerformedEstimate: input.subscription.type === "recurring" ? input.lastPerformedEstimate ?? void 0 : void 0,
      // Stored as a date-only LocalDate string (no time) — see LocalDateSchema.
      lastPerformedAt: input.subscription.type === "recurring" ? input.lastPerformedAt ?? void 0 : void 0,
      // One-time reminders fire on a user-picked calendar day, stored directly as that
      // LocalDate. Recurring reminders derive their date from lastPerformedEstimate + interval.
      nextTaskDate: input.subscription.type === "one-time" && input.scheduledDate ? input.scheduledDate : void 0,
      createdAt: nowIso,
      createdBy: ctx.userId,
      updatedAt: nowIso
    };
    await homeTaskSubscription.setHomeTaskSubscription(ctx.homeId, subscriptionId, homeSubscription);
    ctx.logger.info("Created custom task subscription", { taskTypeId, subscriptionId });
    return { taskTypeId, subscriptionId };
  }
});

// src/home/create-equipment.action.ts
import {
  planStartersOnEquipmentCreated,
  primaryImage,
  SpaceIdSchema as SpaceIdSchema2,
  toImageCollection
} from "@rock/types";

// src/validators/validateEquipmentTypeId.ts
async function validateEquipmentTypeId(typeId, ctx) {
  const { equipmentType, customEquipmentType } = ctx.daos;
  const type = await equipmentType.resolveTypeById(typeId, ctx.homeId, customEquipmentType);
  if (!type) {
    throw new ActionError({
      userMessage: `Invalid equipment type, please try again.`,
      chatMessage: `We couldn't find equipment type "${typeId}". Resolve it via list_equipment_types and try again.`
    });
  }
  return type;
}

// src/validators/validateFolderId.ts
async function validateFolderId(folderId, ctx) {
  const matched = await ctx.daos.folder.getFolder(ctx.homeId, folderId);
  if (!matched?.active) {
    throw new ActionError({
      userMessage: `That folder doesn't exist anymore.`,
      chatMessage: `folderId "${folderId}" was not found (or is deleted) in this home. Caller must create or resolve the folder before placing equipment in it.`
    });
  }
}

// src/validators/validateRoomId.ts
async function validateRoomId(roomId, ctx) {
  const matched = await ctx.daos.room.getRoom(ctx.homeId, roomId);
  if (!matched) {
    throw new ActionError({
      userMessage: `Hmm, we don't see that room in your home.`,
      chatMessage: `roomId "${roomId}" did not match any room in this home. Ask the user which room from the home context's rooms list, or omit roomId.`
    });
  }
}

// src/home/equipment-image.helpers.ts
import {
  highlightForCategory,
  withHighlightForCategory
} from "@rock/types";
function applyCategoryImage(images, category, image) {
  if (image === void 0) return images;
  const stripped = images.map((img) => (img.categories ?? []).includes(category) ? withoutCategory(img, category) : img).filter((img) => !isOrphanCodeImage(img));
  if (image === null) return stripped;
  const existing = stripped.find((img) => img.id === image.id);
  if (existing) {
    const merged = [.../* @__PURE__ */ new Set([...existing.categories ?? [], category])];
    const box = highlightForCategory(image, category);
    return stripped.map(
      (img) => img.id === image.id ? withCategories(withHighlightForCategory(img, category, box), merged) : img
    );
  }
  return [...stripped, withCategories(image, [category])];
}
function withoutCategory(image, category) {
  return withCategories(
    image,
    (image.categories ?? []).filter((c) => c !== category)
  );
}
function withCategories(image, categories) {
  return { ...image, categories };
}
function isOrphanCodeImage(image) {
  return image.categories !== void 0 && image.categories.length === 0;
}
function droppedCodeImages(before, after) {
  return before.filter((img) => (img.categories?.length ?? 0) > 0 && !after.some((next) => next.id === img.id));
}

// src/home/equipment-refresh.helpers.ts
async function triggerEquipmentDetailsRefresh(args) {
  const { equipment, equipmentRefresh, ctx, equipmentId, clearStale } = args;
  if (clearStale) {
    await equipment.clearModelDetails(ctx.homeId, equipmentId);
  }
  const refreshId = ctx.idGen();
  await equipment.updateRefreshId(ctx.homeId, equipmentId, refreshId);
  const refresh = {
    object: "task-refresh",
    schema: 1,
    id: refreshId,
    type: "equipment-details",
    status: "requested",
    // Stamped at request time so the doc carries a freshness clock from its very first write.
    // The UI ages an unfinished refresh out against this; a doc without one can never expire,
    // and an offline write replayed after a crash resurrects `requested` with no server fields.
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  };
  await equipmentRefresh.setEquipmentRefresh(ctx.homeId, equipmentId, refreshId, refresh);
  ctx.logger.info("Requested equipment refresh", { equipmentId, refreshId });
}

// src/home/create-equipment.action.ts
var createEquipmentAction = defineAction({
  name: "create-equipment",
  input: CreateEquipmentInputSchema,
  // Business rules beyond what the schema enforces. Runs before `execute`
  // (via the defineAction wrapper) AND directly from chat-draft mode — so
  // surfaces never re-implement these checks. ActionError carries both
  // userMessage (toast/UI) and chatMessage (AI-to-AI guidance like "use
  // list_equipment_types").
  validate: async (input, ctx) => {
    const [typeResult, roomResult, folderResult] = await Promise.allSettled([
      validateEquipmentTypeId(input.typeId, ctx),
      input.roomId ? validateRoomId(input.roomId, ctx) : Promise.resolve(),
      input.folderId ? validateFolderId(input.folderId, ctx) : Promise.resolve()
    ]);
    if (typeResult.status === "rejected") throw typeResult.reason;
    const type = typeResult.value;
    if (type.scope === "system") {
      throw new ActionError({
        userMessage: `${type.title} is a system-level grouping rather than a single piece of equipment.`,
        chatMessage: `Type "${input.typeId}" is system-scope. Pick a specific component type instead \u2014 never call create-equipment with a system-scope typeId.`
      });
    }
    if (roomResult.status === "rejected") throw roomResult.reason;
    if (folderResult.status === "rejected") throw folderResult.reason;
  },
  execute: async (input, ctx) => {
    const { equipment, equipmentRefresh, guideStarter, home } = ctx.daos;
    const { type, targetFolder, starters, folders, currentHome } = await loadCreateEquipmentContext(input, ctx);
    const resolvedSpace = input.spaceId ?? type?.spaceId;
    if (!resolvedSpace) {
      throw new ActionError({
        userMessage: "We could not determine where to add this equipment.",
        chatMessage: `No space resolved for type "${input.typeId}". Resolve it via list_equipment_types (its record carries a default space) and retry.`
      });
    }
    const spaceId = SpaceIdSchema2.parse(resolvedSpace);
    if (targetFolder && targetFolder.spaceId !== spaceId) {
      throw new ActionError({
        userMessage: "That folder is in a different area of your home.",
        chatMessage: `folderId "${input.folderId}" lives in space "${targetFolder.spaceId}" but the equipment resolves to "${spaceId}" \u2014 equipment can only join folders in its own space.`
      });
    }
    const equipmentId = input.equipmentId ?? ctx.idGen();
    let stampedImages = [];
    if (input.image) {
      stampedImages.push(input.image);
    } else {
      const typeImage = type ? primaryImage(type) : void 0;
      if (typeImage) stampedImages.push({ ...typeImage, id: ctx.idGen() });
    }
    stampedImages = applyCategoryImage(stampedImages, "model-number", input.modelNumberImage);
    stampedImages = applyCategoryImage(stampedImages, "serial-number", input.serialNumberImage);
    const equipmentDoc = {
      object: "equipment",
      schema: 3,
      id: equipmentId,
      active: true,
      status: "active",
      homeId: ctx.homeId,
      spaceId,
      typeId: input.typeId,
      // Root by default — display groups root equipment at render time. A folder id means
      // the caller pre-resolved a system instance or user folder (validated above).
      ...input.folderId ? { folderId: input.folderId } : {},
      createdAt: (/* @__PURE__ */ new Date()).toISOString(),
      createdBy: ctx.userId,
      nickname: input.nickname ?? void 0,
      ...stampedImages.length > 0 ? toImageCollection(stampedImages) : {},
      // Curated subset (chat-friendly) merges first, then any
      // arbitrary `input.fields` (guide-derived `quantity`, etc.)
      // override / extend.
      fields: {
        ...input.roomId ? { roomId: input.roomId } : {},
        ...input.modelNumber ? { modelNumber: input.modelNumber } : {},
        ...input.brand ? { brand: input.brand } : {},
        ...input.productName ? { productName: input.productName } : {},
        ...input.fields ?? {}
      }
    };
    await equipment.setEquipment(ctx.homeId, equipmentId, equipmentDoc);
    ctx.logger.info("Created equipment", { equipmentId, typeId: input.typeId, spaceId });
    ctx.analytics?.track("Equipment Created", {
      homeId: ctx.homeId,
      spaceId,
      folderId: input.folderId ?? void 0,
      equipmentId
    });
    try {
      await completeSatisfiedStarters({
        guideStarter,
        homeId: ctx.homeId,
        equipment: equipmentDoc,
        starters,
        folders
      });
    } catch (err) {
      ctx.logger.error("Error completing satisfied starters after create", { err, equipmentId });
    }
    const fields = equipmentDoc.fields;
    if (fields?.modelNumber || fields?.productName || fields?.brand?.trim()) {
      await triggerEquipmentDetailsRefresh({
        equipment,
        equipmentRefresh,
        ctx,
        equipmentId,
        clearStale: false
      });
    }
    await markSpaceFinishedIfNeeded({ home, homeId: ctx.homeId, spaceId, current: currentHome });
    return { equipment: equipmentDoc };
  }
});
async function loadCreateEquipmentContext(input, ctx) {
  const { equipmentType, customEquipmentType, folder, guideStarter, home } = ctx.daos;
  const [type, targetFolder, starters, folders, currentHome] = await Promise.all([
    equipmentType.resolveTypeById(input.typeId, ctx.homeId, customEquipmentType),
    input.folderId ? folder.getFolder(ctx.homeId, input.folderId) : void 0,
    guideStarter.listGuideStarters(ctx.homeId),
    // Skipped entirely for root equipment, as `completeSatisfiedStarters` always has.
    input.folderId ? folder.listFolders(ctx.homeId) : [],
    // Read before the write rather than after: the space summary it feeds is independent of the
    // equipment doc (only `blockCount` carries over, and nothing recomputes that on a create),
    // and this client is the only writer during a guide finish.
    home.getHome(ctx.homeId)
  ]);
  return { type, targetFolder, starters, folders, currentHome };
}
async function completeSatisfiedStarters(args) {
  const { guideStarter, homeId, equipment, starters, folders } = args;
  if (starters.length === 0) return;
  const satisfied = planStartersOnEquipmentCreated(starters, equipment, folders);
  for (const starter of satisfied) {
    await guideStarter.completeGuideStarter(homeId, starter.id);
  }
}
async function markSpaceFinishedIfNeeded(args) {
  const { home, homeId, spaceId, current } = args;
  if (!current) return;
  const summary = current.spaceSummary?.[spaceId];
  if (summary?.status === "finished" && summary.finishedVia !== "skipped") return;
  await home.putSpaceSummary(homeId, spaceId, {
    ...summary,
    blockCount: summary?.blockCount ?? 0,
    status: "finished",
    finishedVia: "equipment-added"
  });
}

// src/home/update-equipment.action.ts
import {
  orderedImages
} from "@rock/types";
var updateEquipmentAction = defineAction({
  name: "update-equipment",
  input: UpdateEquipmentInputSchema,
  // Business rules. Runs before `execute` (via the defineAction wrapper) and
  // directly from chat-draft mode. A no-fields call is a successful no-op,
  // not an error — callers can fire-and-forget without checking ahead.
  // `input.spaceId` is accepted but IGNORED for addressing (kept in the schema for in-flight
  // chat drafts and older clients): equipment is addressed by (homeId, equipmentId), and the
  // doc's own `spaceId` field is the only space truth.
  validate: async (input, ctx) => {
    await validateEquipmentId(input.equipmentId, ctx);
    if (input.roomId) {
      await validateRoomId(input.roomId, ctx);
    }
    if (input.folderId) {
      await validateFolderId(input.folderId, ctx);
    }
  },
  execute: async (input, ctx) => {
    const { equipment, equipmentRefresh } = ctx.daos;
    const current = await equipment.getEquipment(ctx.homeId, input.equipmentId);
    const fieldChanges = {};
    if (input.modelNumber) fieldChanges.modelNumber = input.modelNumber;
    if (input.brand) fieldChanges.brand = input.brand;
    if (input.productName) fieldChanges.productName = input.productName;
    if (input.roomId) fieldChanges.roomId = input.roomId;
    if (input.fields) Object.assign(fieldChanges, input.fields);
    const brandChanged = "brand" in fieldChanges && brandText(fieldChanges.brand) !== brandText(current?.fields?.brand);
    if (brandChanged && !("brandRef" in fieldChanges)) {
      fieldChanges.brandRef = void 0;
    }
    const intent = nicknameIntent(input.nickname);
    const updates = {};
    if (intent === "set") updates.nickname = input.nickname.trim();
    if (input.active !== void 0 && input.active !== null) {
      if (current?.status === "deleted") {
        ctx.logger.warn("Ignoring an active change on equipment that was removed for good", {
          equipmentId: input.equipmentId
        });
      } else {
        updates.active = input.active;
        updates.status = input.active ? "active" : "archived";
        if (input.active) {
          await equipment.clearArchiveStamps(ctx.homeId, input.equipmentId);
        } else {
          updates.archivedAt = (/* @__PURE__ */ new Date()).toISOString();
          updates.archivedBy = ctx.userId;
        }
      }
    }
    const folderChanged = input.folderId !== void 0;
    if (input.folderId !== void 0) {
      if (input.folderId) {
        const folder = await ctx.daos.folder.getFolder(ctx.homeId, input.folderId);
        if (folder && current && folder.spaceId !== current.spaceId) {
          throw new ActionError({
            userMessage: "That folder is in a different area of your home.",
            chatMessage: `folderId "${input.folderId}" lives in space "${folder.spaceId}" but the equipment is in "${current.spaceId}" \u2014 equipment can only join folders in its own space.`
          });
        }
      }
      await equipment.setEquipmentFolder(ctx.homeId, input.equipmentId, input.folderId ?? void 0);
    }
    if (input.serialConfirmation) updates.serialConfirmation = input.serialConfirmation;
    if (input.hiddenCaptureFields) updates.hiddenCaptureFields = input.hiddenCaptureFields;
    if (Object.keys(fieldChanges).length > 0) {
      updates.fields = { ...current?.fields ?? {}, ...fieldChanges };
    }
    let removedImages = [];
    let imagesChanged = false;
    if (input.modelNumberImage !== void 0 || input.serialNumberImage !== void 0) {
      const before = orderedImages(current ?? {});
      let images = before;
      images = applyCategoryImage(images, "model-number", input.modelNumberImage);
      images = applyCategoryImage(images, "serial-number", input.serialNumberImage);
      removedImages = droppedCodeImages(before, images);
      imagesChanged = await writeImageDiff({
        equipment,
        homeId: ctx.homeId,
        equipmentId: input.equipmentId,
        before,
        after: images
      });
    }
    if (Object.keys(updates).length > 0) {
      await equipment.updateEquipment(ctx.homeId, input.equipmentId, updates);
    }
    if (intent === "clear") {
      await equipment.updateNickname(ctx.homeId, input.equipmentId, "");
    }
    const clearSerialConfirmation = input.serialConfirmation === null;
    if (clearSerialConfirmation) {
      await equipment.clearSerialConfirmation(ctx.homeId, input.equipmentId);
    }
    const anythingChanged = Object.keys(updates).length > 0 || intent === "clear" || clearSerialConfirmation || imagesChanged || folderChanged;
    if (anythingChanged) {
      ctx.logger.info("Updated equipment", {
        equipmentId: input.equipmentId,
        updates,
        imagesChanged,
        ...folderChanged ? { folderId: input.folderId } : {}
      });
      const trackedFields = [
        ...Object.keys(updates).filter((k) => k !== "fields"),
        ...Object.keys(fieldChanges),
        ...intent === "clear" ? ["nickname"] : [],
        ...clearSerialConfirmation ? ["serialConfirmation"] : [],
        ...imagesChanged ? ["images"] : [],
        ...folderChanged ? ["folderId"] : []
      ];
      ctx.analytics?.track("Equipment Updated", {
        homeId: ctx.homeId,
        spaceId: current?.spaceId,
        equipmentId: input.equipmentId,
        fields: trackedFields
      });
    }
    const identityChanged = ["modelNumber", "productName"].some(
      (key) => !!fieldChanges[key] && fieldChanges[key] !== current?.fields?.[key]
    );
    const refChanged = "brandRef" in fieldChanges && fieldChanges.brandRef?.brandId !== current?.fields?.brandRef?.brandId;
    if (identityChanged || brandChanged || refChanged) {
      await triggerEquipmentDetailsRefresh({
        equipment,
        equipmentRefresh,
        ctx,
        equipmentId: input.equipmentId,
        clearStale: identityChanged
      });
    }
    return {
      equipmentId: input.equipmentId,
      ...removedImages.length > 0 ? { removedImages } : {}
    };
  }
});
function brandText(brand) {
  return brand?.trim() ?? "";
}
function nicknameIntent(nickname) {
  if (nickname === void 0 || nickname === null) return "leave";
  return nickname.trim() ? "set" : "clear";
}
async function writeImageDiff(args) {
  const { equipment, homeId, equipmentId, before, after } = args;
  const beforeById = new Map(before.map((img) => [img.id, img]));
  const afterIds = new Set(after.map((img) => img.id));
  let wrote = false;
  for (const img of after) {
    const prev = beforeById.get(img.id);
    if (!prev) {
      await equipment.putImage(homeId, equipmentId, img);
      wrote = true;
      continue;
    }
    const patch = survivingImagePatch(prev, img);
    if (patch) {
      await equipment.updateImageFields(homeId, equipmentId, img.id, patch);
      wrote = true;
    }
  }
  for (const img of before) {
    if (!afterIds.has(img.id)) {
      await equipment.removeImage(homeId, equipmentId, img.id);
      wrote = true;
    }
  }
  return wrote;
}
function survivingImagePatch(prev, next) {
  const patch = {};
  if (!sameCategories(prev.categories, next.categories)) patch.categories = next.categories;
  if (next.highlights && !sameHighlights(prev.highlights, next.highlights)) patch.highlights = next.highlights;
  return Object.keys(patch).length > 0 ? patch : void 0;
}
function sameCategories(a, b) {
  const left = a ?? [];
  const right = b ?? [];
  return left.length === right.length && left.every((category, i) => category === right[i]);
}
function sameHighlights(a, b) {
  const left = a ?? [];
  const right = b ?? [];
  return left.length === right.length && left.every((box, i) => {
    const other = right[i];
    return box.x === other.x && box.y === other.y && box.width === other.width && box.height === other.height;
  });
}

// src/reminders/adjust-reminder-frequency.action.ts
import { calculateNextTaskDate, resolveLastPerformedAt, withFittingWindow } from "@rock/tasks";

// src/validators/validateSubscriptionId.ts
async function validateSubscriptionId(subscriptionId, ctx) {
  const subscription = await ctx.daos.homeTaskSubscription.getHomeTaskSubscription(ctx.homeId, subscriptionId);
  if (!subscription) {
    throw new ActionError({
      userMessage: `We couldn't find that reminder.`,
      chatMessage: `subscriptionId "${subscriptionId}" was not found in home "${ctx.homeId}". Copy the subscriptionId from a reminders entry in the home context.`
    });
  }
  return subscription;
}

// src/reminders/adjust-reminder-frequency.action.ts
var adjustReminderFrequencyAction = defineAction({
  name: "adjust-reminder-frequency",
  input: AdjustReminderFrequencyInputSchema,
  validate: async (input, ctx) => {
    const subscription = await validateSubscriptionId(input.subscriptionId, ctx);
    if (subscription.subscription.type !== "recurring") {
      throw new ActionError({
        userMessage: `Only recurring reminders have a frequency to adjust.`,
        chatMessage: `subscriptionId "${input.subscriptionId}" is a one-time reminder, which has no cadence. Use \`action-reschedule-reminder\` to move its date, or leave it alone.`
      });
    }
  },
  execute: async (input, ctx) => {
    const subscription = await validateSubscriptionId(input.subscriptionId, ctx);
    const nowIso = (/* @__PURE__ */ new Date()).toISOString();
    const newSubscription = withFittingWindow({
      type: "recurring",
      interval: input.interval,
      offset: subscription.subscription.type === "recurring" ? subscription.subscription.offset : void 0,
      window: subscription.subscription.window
    });
    const newNextTaskDate = calculateNextTaskDate(
      newSubscription,
      subscription.createdAt,
      subscription.lastGeneratedAt,
      resolveLastPerformedAt(subscription) ?? null
    );
    await ctx.daos.homeTaskSubscription.updateHomeTaskSubscription(ctx.homeId, input.subscriptionId, {
      subscription: newSubscription,
      nextTaskDate: newNextTaskDate ?? null,
      updatedAt: nowIso
    });
    ctx.logger.info("Adjusted reminder frequency", {
      subscriptionId: input.subscriptionId,
      interval: input.interval,
      nextTaskDate: newNextTaskDate
    });
    ctx.analytics?.track("Reminder Frequency Adjusted", {
      homeId: ctx.homeId,
      subscriptionId: input.subscriptionId,
      intervalValue: input.interval.value,
      intervalUnit: input.interval.unit
    });
    return { subscriptionId: input.subscriptionId, nextTaskDate: newNextTaskDate };
  }
});

// src/reminders/pause-reminder.action.ts
var pauseReminderAction = defineAction({
  name: "pause-reminder",
  input: PauseReminderInputSchema,
  validate: async (input, ctx) => {
    const subscription = await validateSubscriptionId(input.subscriptionId, ctx);
    if (subscription.status === "paused") {
      throw new ActionError({
        userMessage: `That reminder is already paused.`,
        chatMessage: `subscriptionId "${input.subscriptionId}" is already paused. Tell the user it's already paused \u2014 no action needed \u2014 or offer to resume it instead.`
      });
    }
  },
  execute: async (input, ctx) => {
    const nowIso = (/* @__PURE__ */ new Date()).toISOString();
    await ctx.daos.homeTaskSubscription.pauseHomeTaskSubscription(
      ctx.homeId,
      input.subscriptionId,
      nowIso,
      ctx.userId
    );
    ctx.logger.info("Paused reminder", { subscriptionId: input.subscriptionId });
    ctx.analytics?.track("Reminder Paused", { homeId: ctx.homeId, subscriptionId: input.subscriptionId });
    return { subscriptionId: input.subscriptionId };
  }
});

// src/reminders/remove-reminder.action.ts
var removeReminderAction = defineAction({
  name: "remove-reminder",
  input: RemoveReminderInputSchema,
  validate: async (input, ctx) => {
    const subscription = await validateSubscriptionId(input.subscriptionId, ctx);
    if (subscription.status === "deleted") {
      throw new ActionError({
        userMessage: `That reminder has already been removed.`,
        chatMessage: `subscriptionId "${input.subscriptionId}" is already marked deleted. No action needed.`
      });
    }
  },
  execute: async (input, ctx) => {
    const nowIso = (/* @__PURE__ */ new Date()).toISOString();
    const pending = await ctx.daos.task.findPendingTasksForSubscription(ctx.homeId, input.subscriptionId);
    for (const task of pending) {
      const skipped = {
        ...task,
        status: "skipped",
        skippedAt: nowIso,
        skippedBy: ctx.userId,
        updatedAt: nowIso
      };
      await ctx.daos.task.setTask(ctx.homeId, task.id, skipped);
    }
    await ctx.daos.homeTaskSubscription.updateHomeTaskSubscription(ctx.homeId, input.subscriptionId, {
      status: "deleted",
      updatedAt: nowIso
    });
    ctx.logger.info("Removed reminder", {
      subscriptionId: input.subscriptionId,
      skippedTaskCount: pending.length
    });
    ctx.analytics?.track("Reminder Removed", {
      homeId: ctx.homeId,
      subscriptionId: input.subscriptionId,
      skippedTaskCount: pending.length
    });
    return { subscriptionId: input.subscriptionId, skippedTaskCount: pending.length };
  }
});

// src/reminders/reschedule-reminder.action.ts
import { cascadeReschedule } from "@rock/tasks";
var rescheduleReminderAction = defineAction({
  name: "reschedule-reminder",
  input: RescheduleReminderInputSchema,
  validate: async (input, ctx) => {
    const subscription = await validateSubscriptionId(input.subscriptionId, ctx);
    const pending = await ctx.daos.task.findPendingTasksForSubscription(ctx.homeId, input.subscriptionId);
    if (pending.length === 0 && !subscription.nextTaskDate) {
      throw new ActionError({
        userMessage: `There's no upcoming reminder to reschedule yet.`,
        chatMessage: `subscriptionId "${input.subscriptionId}" has no pending task and no scheduled nextTaskDate \u2014 there's nothing to move. The user can adjust the cadence instead via \`action-adjust-reminder-frequency\`.`
      });
    }
  },
  execute: async (input, ctx) => {
    const subscription = await validateSubscriptionId(input.subscriptionId, ctx);
    const pending = await ctx.daos.task.findPendingTasksForSubscription(ctx.homeId, input.subscriptionId);
    const sorted = [...pending].sort((a, b) => a.taskDate.localeCompare(b.taskDate));
    const [soonest, ...future] = sorted;
    const nowIso = (/* @__PURE__ */ new Date()).toISOString();
    if (!soonest) {
      await ctx.daos.homeTaskSubscription.updateHomeTaskSubscription(ctx.homeId, subscription.id, {
        nextTaskDate: input.newTaskDate,
        updatedAt: nowIso
      });
      ctx.logger.info("Rescheduled reminder (nextTaskDate)", {
        subscriptionId: input.subscriptionId,
        from: subscription.nextTaskDate,
        to: input.newTaskDate
      });
      ctx.analytics?.track("Reminder Rescheduled", {
        homeId: ctx.homeId,
        subscriptionId: input.subscriptionId,
        taskId: null,
        futureShifted: 0
      });
      return { taskId: null, newTaskDate: input.newTaskDate, futureTaskCount: 0 };
    }
    const result = cascadeReschedule({
      task: soonest,
      newTaskDate: input.newTaskDate,
      subscription,
      futurePendingTasks: future,
      nowIso
    });
    await ctx.daos.task.setTask(ctx.homeId, soonest.id, result.updatedTask);
    for (const futureTask of result.updatedFutureTasks) {
      await ctx.daos.task.setTask(ctx.homeId, futureTask.id, futureTask);
    }
    if (result.subscriptionNextTaskDate) {
      await ctx.daos.homeTaskSubscription.updateHomeTaskSubscription(ctx.homeId, subscription.id, {
        nextTaskDate: result.subscriptionNextTaskDate,
        updatedAt: nowIso
      });
    }
    ctx.logger.info("Rescheduled reminder", {
      subscriptionId: input.subscriptionId,
      taskId: soonest.id,
      from: soonest.taskDate,
      to: input.newTaskDate,
      futureShifted: result.updatedFutureTasks.length
    });
    ctx.analytics?.track("Reminder Rescheduled", {
      homeId: ctx.homeId,
      subscriptionId: input.subscriptionId,
      taskId: soonest.id,
      futureShifted: result.updatedFutureTasks.length
    });
    return {
      taskId: soonest.id,
      newTaskDate: input.newTaskDate,
      futureTaskCount: result.updatedFutureTasks.length
    };
  }
});

// src/reminders/resume-reminder.action.ts
import { dateToLocalDate } from "@rock/util";

// src/reminders/soonestByTaskDate.ts
function soonestByTaskDate(tasks) {
  return tasks.reduce((a, b) => a.taskDate <= b.taskDate ? a : b);
}

// src/reminders/resume-reminder.action.ts
var resumeReminderAction = defineAction({
  name: "resume-reminder",
  input: ResumeReminderInputSchema,
  validate: async (input, ctx) => {
    const subscription = await validateSubscriptionId(input.subscriptionId, ctx);
    if (subscription.status === "active") {
      throw new ActionError({
        userMessage: `That reminder is already active.`,
        chatMessage: `subscriptionId "${input.subscriptionId}" is already active. No action needed.`
      });
    }
    if (subscription.status === "deleted") {
      throw new ActionError({
        userMessage: `That reminder has been removed.`,
        chatMessage: `subscriptionId "${input.subscriptionId}" was removed and cannot be resumed. Offer to recreate the reminder with \`action-create-custom-task\` instead.`
      });
    }
    if (subscription.status === "orphaned") {
      throw new ActionError({
        userMessage: `That reminder is waiting on equipment that's turned off.`,
        chatMessage: `subscriptionId "${input.subscriptionId}" is orphaned because its equipment is inactive. Tell the user to reactivate the equipment; resuming the reminder cannot fix it.`
      });
    }
  },
  execute: async (input, ctx) => {
    const now = /* @__PURE__ */ new Date();
    const nowIso = now.toISOString();
    await ctx.daos.homeTaskSubscription.resumeHomeTaskSubscription(ctx.homeId, input.subscriptionId, nowIso);
    ctx.logger.info("Resumed reminder", { subscriptionId: input.subscriptionId });
    ctx.analytics?.track("Reminder Resumed", { homeId: ctx.homeId, subscriptionId: input.subscriptionId });
    const pulledForwardTaskId = await pullForwardOverdueTask(
      input.subscriptionId,
      dateToLocalDate(now),
      nowIso,
      ctx
    );
    return { subscriptionId: input.subscriptionId, pulledForwardTaskId };
  }
});
async function pullForwardOverdueTask(subscriptionId, today, nowIso, ctx) {
  const pending = await ctx.daos.task.findPendingTasksForSubscription(ctx.homeId, subscriptionId);
  if (pending.length === 0) return null;
  const soonest = soonestByTaskDate(pending);
  if (soonest.taskDate >= today) return null;
  await ctx.daos.task.updateTask(ctx.homeId, soonest.id, { taskDate: today, updatedAt: nowIso });
  ctx.logger.info("Pulled overdue reminder task forward on resume", {
    subscriptionId,
    taskId: soonest.id,
    from: soonest.taskDate
  });
  return soonest.id;
}

// src/reminders/skip-next-reminder.action.ts
var skipNextReminderAction = defineAction({
  name: "skip-next-reminder",
  input: SkipNextReminderInputSchema,
  validate: async (input, ctx) => {
    await validateSubscriptionId(input.subscriptionId, ctx);
    const pending = await ctx.daos.task.findPendingTasksForSubscription(ctx.homeId, input.subscriptionId);
    if (pending.length === 0) {
      throw new ActionError({
        userMessage: `There's no upcoming reminder to skip yet.`,
        chatMessage: `subscriptionId "${input.subscriptionId}" has no pending tasks \u2014 the next occurrence hasn't been generated yet. Tell the user there's nothing to skip; they can pause the reminder instead if they want it off for a while.`
      });
    }
  },
  execute: async (input, ctx) => {
    const pending = await ctx.daos.task.findPendingTasksForSubscription(ctx.homeId, input.subscriptionId);
    const soonest = soonestByTaskDate(pending);
    const nowIso = (/* @__PURE__ */ new Date()).toISOString();
    const updated = {
      ...soonest,
      status: "skipped",
      skippedAt: nowIso,
      skippedBy: ctx.userId,
      updatedAt: nowIso
    };
    await ctx.daos.task.setTask(ctx.homeId, soonest.id, updated);
    ctx.logger.info("Skipped next reminder occurrence", {
      subscriptionId: input.subscriptionId,
      taskId: soonest.id
    });
    ctx.analytics?.track("Reminder Next Skipped", {
      homeId: ctx.homeId,
      subscriptionId: input.subscriptionId,
      taskId: soonest.id
    });
    return { taskId: soonest.id, taskDate: soonest.taskDate };
  }
});

// src/support/submit-feedback.action.ts
var UNKNOWN_CLIENT_APP = {
  env: "unknown",
  version: "unknown",
  platform: "unknown",
  platformId: "unknown"
};
var submitFeedbackAction = defineAction({
  name: "submit-feedback",
  input: SubmitFeedbackInputSchema,
  execute: async (input, ctx) => {
    const submissionId = ctx.idGen();
    const user = await ctx.daos.user.getUser(ctx.userId);
    const submission = {
      object: "feedback-submission",
      schema: 1,
      id: submissionId,
      userId: ctx.userId,
      homeId: ctx.homeId,
      // Undefined on form-path calls; the db-server adapter strips it.
      chatConversationId: ctx.chatConversationId,
      body: normalizeBody(input.body),
      equipment: input.equipment ?? void 0,
      contactEmail: user?.email,
      createdAt: (/* @__PURE__ */ new Date()).toISOString(),
      app: ctx.clientApp ?? UNKNOWN_CLIENT_APP
    };
    await ctx.daos.feedbackSubmission.setFeedbackSubmission(submissionId, submission);
    ctx.logger.info("Feedback submission created", { submissionId, type: submission.body.type });
    ctx.analytics?.track("Feedback Submitted", { submissionId, type: submission.body.type });
    return { submissionId };
  }
});
function normalizeBody(body) {
  const out = {};
  for (const [k, v] of Object.entries(body)) {
    if (v !== null) out[k] = v;
  }
  return out;
}

// src/dispatchCommit.ts
async function dispatchCommit(args, ctx) {
  switch (args.action) {
    case "create-equipment":
      return createEquipmentAction.execute(args.input, ctx);
    case "update-equipment":
      return updateEquipmentAction.execute(args.input, ctx);
    case "create-custom-task":
      return createCustomTaskAction.execute(args.input, ctx);
    case "submit-feedback":
      return submitFeedbackAction.execute(args.input, ctx);
    case "pause-reminder":
      return pauseReminderAction.execute(args.input, ctx);
    case "resume-reminder":
      return resumeReminderAction.execute(args.input, ctx);
    case "adjust-reminder-frequency":
      return adjustReminderFrequencyAction.execute(args.input, ctx);
    case "skip-next-reminder":
      return skipNextReminderAction.execute(args.input, ctx);
    case "reschedule-reminder":
      return rescheduleReminderAction.execute(args.input, ctx);
    case "remove-reminder":
      return removeReminderAction.execute(args.input, ctx);
    default:
      assertUnreachable(args.action);
  }
}

// src/considerations/accept-considerations.action.ts
import { recordedEquipmentIds } from "@rock/hql";
import {
  buildGeneratedTask,
  firstOccurrenceDate,
  firstTaskId,
  planTaskGeneration,
  taskGenerationDay
} from "@rock/tasks";
import {
  HomeTaskSubscriptionIdSchema as HomeTaskSubscriptionIdSchema7
} from "@rock/types";
import { resolveReviewStamp } from "@rock/util";

// src/considerations/accept-considerations.types.ts
import {
  ConsiderationSchema,
  LastPerformedEstimateSchema as LastPerformedEstimateSchema2,
  LocalDateSchema as LocalDateSchema3,
  TaskTypeSchema,
  TimestampSchema
} from "@rock/types";
import * as z12 from "zod";
var AcceptConsiderationsInputSchema = z12.object({
  items: z12.array(
    z12.object({
      /** The pending suggestion, as the caller last read it. */
      consideration: ConsiderationSchema,
      /**
       * Its live task types; each one with a cadence becomes a reminder, placed on its own: the
       * review's smear and Add To My List both plan each chore of a suggestion as its own item.
       */
      taskTypes: z12.array(
        TaskTypeSchema.pick({ id: true, subscription: true, effort: true }).extend({
          /**
           * Overrides this reminder's first occurrence, recurring and one-time alike — the review
           * passes the weekend its smear chose for the task type (`planFirstSchedules`), so a whole
           * plan doesn't land on week one. Omitted, the task type computes its own.
           */
          firstTaskDate: LocalDateSchema3.optional(),
          /**
           * A floor under this reminder's first occurrence: it takes the later of this and its own
           * date, so a reminder keeps its cadence. Add To My List passes the weekend its planner
           * chose for the task type (`planAddToList`); `firstTaskDate`, when given, overrides outright.
           */
          notBefore: LocalDateSchema3.optional()
        })
      ),
      /** "When did you last do this?" — anchors a recurring reminder's first occurrence. */
      lastPerformedEstimate: LastPerformedEstimateSchema2.optional()
    })
  ),
  /** One instant for every stamp, and the clock the first tasks are generated against. */
  now: TimestampSchema
});

// src/considerations/accept-considerations.action.ts
var acceptConsiderationsAction = defineAction({
  name: "accept-considerations",
  input: AcceptConsiderationsInputSchema,
  execute: async (input, ctx) => {
    const today = await resolveHomeDay(ctx, new Date(input.now));
    const outcomes = await Promise.all(input.items.map((item) => acceptOne(item, input.now, today, ctx)));
    const result = { accepted: [], subscriptionsCreated: 0, firstTasksCreated: 0 };
    for (const outcome of outcomes) {
      if (!outcome) continue;
      result.accepted.push(outcome.considerationId);
      result.subscriptionsCreated += outcome.subscriptionsCreated;
      result.firstTasksCreated += outcome.firstTasksCreated;
    }
    return result;
  }
});
function considerationSubscriptionId(considerationId, taskTypeId) {
  return HomeTaskSubscriptionIdSchema7.parse(`${considerationId}_${taskTypeId}`);
}
async function acceptOne(item, nowIso, today, ctx) {
  const { consideration } = item;
  try {
    if (consideration.homeId !== ctx.homeId) {
      throw new Error(`Consideration belongs to home ${consideration.homeId}, not ${ctx.homeId}`);
    }
    const reminders = await buildNewReminders(item, nowIso, today, ctx);
    const firstTasks = reminders.flatMap((r) => r.firstTask ? [r.firstTask] : []);
    await ctx.daos.consideration.acceptConsiderationWithReminders(ctx.homeId, consideration.id, {
      updates: {
        status: "accepted",
        acceptedAt: nowIso,
        decidedBy: ctx.userId,
        updatedAt: nowIso,
        // Answering it is having seen it: an unread flag left on an answered row would keep the
        // Home tab's dot lit with nothing on the dashboard left to clear it.
        read: true,
        ...resolveReviewStamp(nowIso)
      },
      subscriptions: reminders.map((r) => r.subscription),
      firstTasks
    });
    ctx.logger.info("Accepted consideration with its reminders", {
      considerationId: consideration.id,
      subscriptionIds: reminders.map((r) => r.subscription.id),
      firstTaskIds: firstTasks.map((t) => t.id)
    });
    return {
      considerationId: consideration.id,
      subscriptionsCreated: reminders.length,
      firstTasksCreated: firstTasks.length
    };
  } catch (err) {
    ctx.logger.error("Failed to accept consideration; skipping", { err, considerationId: consideration.id });
    return void 0;
  }
}
async function buildNewReminders(item, nowIso, today, ctx) {
  const { consideration, taskTypes } = item;
  const reminders = await Promise.all(
    taskTypes.map(async (taskType) => {
      const { id: taskTypeId, subscription: cadence } = taskType;
      if (!cadence) {
        ctx.logger.warn("Task type has no subscription", { considerationId: consideration.id, taskTypeId });
        return void 0;
      }
      const subscriptionId = considerationSubscriptionId(consideration.id, taskTypeId);
      if (await ctx.daos.homeTaskSubscription.getHomeTaskSubscription(ctx.homeId, subscriptionId)) {
        ctx.logger.info("Subscription already exists for this suggestion; leaving it as it is", {
          considerationId: consideration.id,
          taskTypeId,
          subscriptionId
        });
        return void 0;
      }
      const subscription = buildSubscription(
        item,
        subscriptionId,
        { ...taskType, subscription: cadence },
        nowIso,
        ctx.userId
      );
      return today ? withFirstTask(subscription, today, nowIso) : { subscription };
    })
  );
  return reminders.filter((r) => !!r);
}
function buildSubscription(item, id, taskType, nowIso, by) {
  const { consideration } = item;
  const { id: taskTypeId, subscription: cadence, effort, firstTaskDate, notBefore } = taskType;
  const recurring = cadence.type === "recurring";
  const lastPerformedEstimate = recurring ? item.lastPerformedEstimate : void 0;
  const recordedUnits = recurring ? void 0 : recordedEquipmentIds(consideration);
  const own = firstTaskDate ?? firstOccurrenceDate(cadence, nowIso, lastPerformedEstimate) ?? void 0;
  return {
    object: "home-task-subscription",
    schema: 1,
    id,
    homeId: consideration.homeId,
    taskTypeId,
    considerationId: consideration.id,
    considerationTypeId: consideration.typeId,
    subscription: cadence,
    effort,
    coveredEquipmentIds: recordedUnits && [...recordedUnits],
    spaceId: consideration.spaceId,
    status: "active",
    lastPerformedEstimate,
    nextTaskDate: notBefore && (!own || own < notBefore) ? notBefore : own,
    createdAt: nowIso,
    createdBy: by,
    updatedAt: nowIso
  };
}
function withFirstTask(subscription, today, nowIso) {
  const step = planTaskGeneration(subscription, today);
  if (step.kind !== "due") {
    return { subscription };
  }
  const { task, subscriptionUpdates } = buildGeneratedTask(subscription, step, {
    taskId: firstTaskId(subscription.id),
    nowIso
  });
  return { subscription: { ...subscription, ...subscriptionUpdates }, firstTask: task };
}
async function resolveHomeDay(ctx, now) {
  try {
    const home = await ctx.daos.home.getHome(ctx.homeId);
    if (home) {
      return taskGenerationDay(home.geo?.timezone, now);
    }
    ctx.logger.warn("Home not found; leaving first tasks to the server", { homeId: ctx.homeId });
  } catch (err) {
    ctx.logger.warn("Could not read the home; leaving first tasks to the server", { homeId: ctx.homeId, err });
  }
  return void 0;
}

// src/considerations/fill-weekend.action.ts
import { pickWeekendRows } from "@rock/tasks";
import { ConsiderationIdSchema, LessonIdSchema } from "@rock/types";

// src/considerations/fill-weekend.types.ts
import { ConsiderationCriticalitySchema, LocalDateSchema as LocalDateSchema4, TimestampSchema as TimestampSchema2 } from "@rock/types";
import * as z13 from "zod";
var WeekendQueueRowSchema = z13.object({
  kind: z13.enum(["recommendations", "lessons"]),
  id: z13.string().min(1),
  criticality: ConsiderationCriticalitySchema.optional(),
  createdAt: TimestampSchema2
});
var FillWeekendInputSchema = z13.object({
  /** The weekend's first day; every pick is stamped with it. */
  weekend: LocalDateSchema4,
  /** The home's local day; an act-now suggestion is stamped with it instead. */
  today: LocalDateSchema4,
  /** One instant for every `updatedAt` stamp. */
  now: TimestampSchema2,
  /**
   * Slots the weekend has left — `WeekendBudget.effortPerWeekend` minus what it already carries
   * (chores and dated suggestions and lessons alike). May be zero or negative.
   */
  capacity: z13.number(),
  /** Every open suggestion and lesson with no weekend yet. */
  queue: z13.array(WeekendQueueRowSchema),
  /** What each kind costs against the weekend — `WeekendBudget.effort`. */
  effort: z13.object({ recommendations: z13.number().positive(), lessons: z13.number().positive() }),
  /** Suggestion and lesson rows the weekend already holds (`weekendCarry.extras`); they count toward the floor. */
  onWeekend: z13.number().int().min(0).optional(),
  /** Rows the weekend carries whatever the capacity, when the queue has them. Default 1. */
  floor: z13.number().int().min(0).optional()
});

// src/considerations/fill-weekend.action.ts
var fillWeekendAction = defineAction({
  name: "fill-weekend",
  input: FillWeekendInputSchema,
  execute: async (input, ctx) => {
    const { capacity, effort, onWeekend, floor, queue } = input;
    const picks = pickWeekendRows(queue, { capacity, effort, onWeekend, floor });
    const result = { picked: [] };
    for (const { row, actNow } of picks) {
      const reviewAt = actNow ? input.today : input.weekend;
      try {
        await schedule(row, reviewAt, input.now, ctx);
        result.picked.push({ kind: row.kind, id: row.id, reviewAt });
      } catch (err) {
        ctx.logger.error("Failed to give a queued row its weekend; skipping", {
          err,
          kind: row.kind,
          id: row.id
        });
      }
    }
    ctx.logger.info("Filled the weekend from the queue", {
      weekend: input.weekend,
      capacity,
      onWeekend,
      queued: queue.length,
      scheduled: result.picked.length
    });
    return result;
  }
});
function schedule(row, reviewAt, now, ctx) {
  if (row.kind === "lessons") {
    return ctx.daos.lesson.scheduleLessonReview(ctx.homeId, LessonIdSchema.parse(row.id), reviewAt, now);
  }
  return ctx.daos.consideration.scheduleConsiderationReview(
    ctx.homeId,
    ConsiderationIdSchema.parse(row.id),
    reviewAt,
    now
  );
}

// src/home/create-custom-equipment-type.action.ts
import {
  CUSTOM_GROUP,
  CUSTOM_ID_PREFIX as CUSTOM_ID_PREFIX2,
  EquipmentTypeCandidateIdSchema,
  isOfferableType
} from "@rock/types";
import { slugify } from "@rock/util";

// src/home/create-custom-equipment-type.types.ts
import { EquipmentTypeDraftSchema } from "@rock/types";
var draft = EquipmentTypeDraftSchema.shape;
var CreateCustomEquipmentTypeInputSchema = EquipmentTypeDraftSchema.extend({
  group: draft.group.nullish(),
  icon: draft.icon.unwrap().nullish(),
  subtitle: draft.subtitle.unwrap().nullish(),
  candidateId: draft.candidateId.unwrap().nullish(),
  aiClassification: draft.aiClassification.unwrap().nullish(),
  description: draft.description.unwrap().nullish()
});

// src/home/create-custom-equipment-type.action.ts
var CUSTOM_ORDER = 99;
var createCustomEquipmentTypeAction = defineAction({
  name: "create-custom-equipment-type",
  input: CreateCustomEquipmentTypeInputSchema,
  execute: async (input, ctx) => {
    const customType = buildCustomEquipmentType(input, ctx);
    await ctx.daos.customEquipmentType.setCustomEquipmentType(ctx.homeId, customType.id, customType);
    ctx.logger.info("Created custom equipment type", {
      typeId: customType.id,
      candidateId: customType.candidateId
    });
    return { customType };
  }
});
function findMatchingCustomType(customTypes, input) {
  const candidateId = candidateIdFor(input);
  return customTypes.find(
    (t) => isOfferableType(t) && t.candidateId === candidateId && sameTitle(t.title, input.title)
  );
}
function buildCustomEquipmentType(input, ctx) {
  return {
    object: "custom-equipment-type",
    schema: 1,
    id: `${CUSTOM_ID_PREFIX2}${ctx.idGen()}`,
    homeId: ctx.homeId,
    createdBy: ctx.userId,
    candidateId: candidateIdFor(input),
    spaceId: input.spaceId,
    title: input.title.trim(),
    subtitle: input.subtitle?.trim() || void 0,
    icon: input.icon ?? void 0,
    group: input.group?.trim() || CUSTOM_GROUP,
    order: CUSTOM_ORDER,
    scope: "normal",
    specs: [],
    inputFields: [],
    systemParts: [],
    images: [],
    sighting: sightingOf(input)
  };
}
function sightingOf(input) {
  const description = input.description?.trim() || void 0;
  const aiClassification = input.aiClassification ?? void 0;
  return description || aiClassification ? { description, aiClassification } : void 0;
}
function candidateIdFor(input) {
  return input.candidateId ?? EquipmentTypeCandidateIdSchema.parse(slugify(input.title));
}
function sameTitle(a, b) {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

// src/home/update-home-options.action.ts
import { WeekendStartSchema as WeekendStartSchema2 } from "@rock/types";

// src/home/update-home-options.types.ts
import { WeekendStartSchema } from "@rock/types";
import * as z14 from "zod";
var UpdateHomeOptionsInputSchema = z14.object({
  name: z14.string().nullish().describe("The home's name. Omitted or null leaves it alone."),
  weekendStart: WeekendStartSchema.nullish().describe(
    "The first day of the home's weekend (0 = Sunday \u2026 6 = Saturday). Omitted or null leaves it alone."
  )
});

// src/home/update-home-options.action.ts
var updateHomeOptionsAction = defineAction({
  name: "update-home-options",
  input: UpdateHomeOptionsInputSchema,
  // The form path calls `execute` without parsing the input, so the bounds are checked here: a day
  // outside 0-6 would fail `HomeSchema` on every later read of the home.
  validate: async (input) => {
    if (input.name?.trim() === "") {
      throw new ActionError({
        userMessage: "Give your home a name.",
        chatMessage: "name must not be blank."
      });
    }
    if (typeof input.weekendStart === "number" && !WeekendStartSchema2.safeParse(input.weekendStart).success) {
      throw new ActionError({
        userMessage: "Pick a day of the week.",
        chatMessage: "weekendStart must be a whole number from 0 (Sunday) to 6 (Saturday)."
      });
    }
  },
  execute: async (input, ctx) => {
    const updates = {};
    const fields = [];
    if (typeof input.name === "string") {
      updates.name = input.name.trim();
      fields.push("name");
    }
    if (typeof input.weekendStart === "number") {
      updates.weekendStart = input.weekendStart;
      fields.push("weekendStart");
    }
    if (fields.length === 0) return { homeId: ctx.homeId, fields };
    await ctx.daos.home.updateHome(ctx.homeId, updates);
    ctx.logger.info("Updated home options", { homeId: ctx.homeId, updates });
    ctx.analytics?.track("Home Options Updated", { homeId: ctx.homeId, fields });
    return { homeId: ctx.homeId, fields };
  }
});

// src/reminders/restore-reminder.action.ts
import { clampTaskDateForward } from "@rock/tasks";
import { dateToLocalDate as dateToLocalDate2 } from "@rock/util";

// src/reminders/restore-reminder.types.ts
import { HomeTaskSubscriptionIdSchema as HomeTaskSubscriptionIdSchema8 } from "@rock/types";
import * as z15 from "zod";
var RestoreReminderInputSchema = z15.object({
  subscriptionId: HomeTaskSubscriptionIdSchema8.describe(
    "The id of the deleted reminder (`HomeTaskSubscription`) to bring back."
  ),
  /**
   * Whether the reminder still has anything to do in the home it is coming back to. The caller
   * answers it with `appliesToHome` from `@rock/hql` — THE rule, the same one the server's coverage
   * trigger uses — because the question needs the whole home (its equipment, its own facts, and every
   * compiled query), which the action layer cannot read.
   *
   * Deliberately a question about the home rather than a status to write: the caller says what is
   * true, the action decides what state that means.
   */
  applies: z15.boolean().describe("Whether the reminder covers anything in the home as it stands now.")
});

// src/reminders/restore-reminder.action.ts
var restoreReminderAction = defineAction({
  name: "restore-reminder",
  input: RestoreReminderInputSchema,
  validate: async (input, ctx) => {
    const subscription = await validateSubscriptionId(input.subscriptionId, ctx);
    if (subscription.status !== "deleted") {
      throw new ActionError({
        userMessage: `That reminder is already back.`,
        chatMessage: `subscriptionId "${input.subscriptionId}" is "${subscription.status}", not deleted. Restore only brings back a deleted reminder; use \`action-resume-reminder\` for a paused one.`
      });
    }
  },
  execute: async (input, ctx) => {
    const status = input.applies ? "active" : "orphaned";
    const now = /* @__PURE__ */ new Date();
    const nowIso = now.toISOString();
    const subscription = await ctx.daos.homeTaskSubscription.getHomeTaskSubscription(
      ctx.homeId,
      input.subscriptionId
    );
    const restoredTaskId = subscription ? await restoreSkippedOccurrence(subscription, dateToLocalDate2(now), nowIso, ctx) : null;
    await ctx.daos.homeTaskSubscription.restoreHomeTaskSubscription(
      ctx.homeId,
      input.subscriptionId,
      status,
      nowIso
    );
    ctx.logger.info("Restored reminder", { subscriptionId: input.subscriptionId, status, restoredTaskId });
    ctx.analytics?.track("Reminder Restored", {
      homeId: ctx.homeId,
      subscriptionId: input.subscriptionId,
      status
    });
    return { subscriptionId: input.subscriptionId, status, restoredTaskId };
  }
});
async function restoreSkippedOccurrence(subscription, today, nowIso, ctx) {
  const skipped = await ctx.daos.task.findSkippedTasksForSubscription(ctx.homeId, subscription.id);
  const fromThisDeletion = skipped.filter((task) => task.skippedAt === subscription.updatedAt);
  if (fromThisDeletion.length === 0) return null;
  const soonest = soonestByTaskDate(fromThisDeletion);
  const { skippedAt, skippedBy, ...keep } = soonest;
  const restored = {
    ...keep,
    status: "pending",
    taskDate: clampTaskDateForward(soonest.taskDate, today),
    updatedAt: nowIso
  };
  await ctx.daos.task.setTask(ctx.homeId, soonest.id, restored);
  ctx.logger.info("Brought back the occurrence a reminder deletion skipped", {
    subscriptionId: subscription.id,
    taskId: soonest.id,
    from: soonest.taskDate
  });
  return soonest.id;
}

// src/reminders/surface-reminder-now.action.ts
import { TaskSchema } from "@rock/types";
import { todayLocalDate } from "@rock/util";

// src/reminders/surface-reminder-now.types.ts
import { HomeTaskSubscriptionIdSchema as HomeTaskSubscriptionIdSchema9 } from "@rock/types";
import * as z16 from "zod";
var SurfaceReminderNowInputSchema = z16.object({
  subscriptionId: HomeTaskSubscriptionIdSchema9.describe(
    "The id of the reminder (`HomeTaskSubscription`) to surface a task for right now \u2014 i.e. the user is ready to do it."
  )
});

// src/reminders/surface-reminder-now.action.ts
var surfaceReminderNowAction = defineAction({
  name: "surface-reminder-now",
  input: SurfaceReminderNowInputSchema,
  validate: async (input, ctx) => {
    await validateSubscriptionId(input.subscriptionId, ctx);
  },
  execute: async (input, ctx) => {
    const subscription = await validateSubscriptionId(input.subscriptionId, ctx);
    const now = /* @__PURE__ */ new Date();
    const nowIso = now.toISOString();
    const today = todayLocalDate(now);
    const pending = await ctx.daos.task.findPendingTasksForSubscription(ctx.homeId, subscription.id);
    if (pending.length > 0) {
      const soonest = soonestByTaskDate(pending);
      await ctx.daos.task.updateTask(ctx.homeId, soonest.id, { taskDate: today, updatedAt: nowIso });
      ctx.logger.info("Pulled pending reminder task forward to now", {
        subscriptionId: subscription.id,
        taskId: soonest.id,
        from: soonest.taskDate
      });
      ctx.analytics?.track("Reminder Surfaced Now", {
        homeId: ctx.homeId,
        subscriptionId: subscription.id,
        taskId: soonest.id,
        created: false
      });
      return { taskId: soonest.id, taskDate: today, created: false };
    }
    const previousCompleted = await ctx.daos.task.findLastCompletedTaskForSubscription(ctx.homeId, subscription.id);
    const taskId = ctx.idGen();
    const task = TaskSchema.decode({
      object: "task",
      schema: 2,
      id: taskId,
      taskTypeId: subscription.taskTypeId,
      considerationTypeId: subscription.considerationTypeId,
      homeId: ctx.homeId,
      subscriptionId: subscription.id,
      previousCompletedTaskId: previousCompleted?.id,
      spaceId: subscription.spaceId,
      contactId: subscription.contactId,
      taskDate: today,
      notes: subscription.taskNotes,
      createdAt: nowIso,
      updatedAt: nowIso
    });
    await ctx.daos.task.setTask(ctx.homeId, taskId, task);
    const advance = { lastGeneratedAt: nowIso, updatedAt: nowIso };
    if (subscription.subscription.type === "one-time") {
      advance.nextTaskDate = null;
    }
    await ctx.daos.homeTaskSubscription.updateHomeTaskSubscription(ctx.homeId, subscription.id, advance);
    ctx.logger.info("Surfaced new reminder task now", {
      subscriptionId: subscription.id,
      taskId,
      previousCompletedTaskId: previousCompleted?.id
    });
    ctx.analytics?.track("Reminder Surfaced Now", {
      homeId: ctx.homeId,
      subscriptionId: subscription.id,
      taskId,
      created: true
    });
    return { taskId, taskDate: today, created: true };
  }
});
export {
  ACTION_NAMES,
  ACTION_TOOL_NAMES,
  AcceptConsiderationsInputSchema,
  ActionError,
  ActionName,
  AdjustReminderFrequencyInputSchema,
  ChatActionCommitFailureSchema,
  ChatActionCommitSuccessSchema,
  ChatActionDraftFailureSchema,
  ChatActionDraftSchema,
  ChatToolEnvelopeSchema,
  CreateCustomEquipmentTypeInputSchema,
  CreateCustomTaskInputSchema,
  CreateEquipmentInputSchema,
  FillWeekendInputSchema,
  PauseReminderInputSchema,
  RemoveReminderInputSchema,
  RescheduleReminderInputSchema,
  RestoreReminderInputSchema,
  ResumeReminderInputSchema,
  SkipNextReminderInputSchema,
  SubmitFeedbackInputSchema,
  SurfaceReminderNowInputSchema,
  UpdateEquipmentBeforeSchema,
  UpdateEquipmentInputSchema,
  UpdateHomeOptionsInputSchema,
  WeekendQueueRowSchema,
  acceptConsiderationsAction,
  actionToolName,
  adjustReminderFrequencyAction,
  considerationSubscriptionId,
  createCustomEquipmentTypeAction,
  createCustomTaskAction,
  createEquipmentAction,
  defineAction,
  dispatchCommit,
  fillWeekendAction,
  findLiveDraft,
  findMatchingCustomType,
  isActionToolName,
  pauseReminderAction,
  removeReminderAction,
  rescheduleReminderAction,
  restoreReminderAction,
  resumeReminderAction,
  skipNextReminderAction,
  snapshotEquipmentBefore,
  submitFeedbackAction,
  surfaceReminderNowAction,
  triggerEquipmentDetailsRefresh,
  updateEquipmentAction,
  updateHomeOptionsAction
};
//# sourceMappingURL=index.js.map